/**
 * siteOverview — the pure logic behind the Customer Overview table (routes/Overview.tsx).
 *
 * Kept out of the component so the Node test runner can pin it without a React harness
 * (siteOverview.test.ts): what "needs attention" means, the client-side search/filter, the
 * worst-first default order, the summary line, the firmware wording, and the CSV export.
 *
 * Every number here comes straight from GET /api/v2/sites/overview; nothing is re-derived
 * (the clients figure in particular is the server's connected-now count, services/client_counts).
 */
import type { SiteFirmwareStatus, SiteOverviewRow } from "@/types/domain";

export type OverviewFilter = "all" | "attention" | "offline";

/** Worst first: the order the table opens on (same ladder as the Customers table). */
export const HEALTH_RANK: Record<SiteOverviewRow["health"], number> = {
  critical: 0,
  warn: 1,
  healthy: 2,
};

/** A site needs attention when anything about it is not clean. */
export function needsAttention(r: SiteOverviewRow): boolean {
  return r.health !== "healthy" || r.alerts_open > 0 || r.devices_offline > 0;
}

/** Client-side search (site, location, customer name) + the quick filter chips. */
export function filterRows(
  rows: SiteOverviewRow[],
  term: string,
  filter: OverviewFilter,
): SiteOverviewRow[] {
  const q = term.trim().toLowerCase();
  return rows.filter((r) => {
    if (filter === "attention" && !needsAttention(r)) return false;
    if (filter === "offline" && r.devices_offline === 0) return false;
    if (!q) return true;
    return (
      r.site_name.toLowerCase().includes(q) ||
      (r.location ?? "").toLowerCase().includes(q) ||
      r.org_name.toLowerCase().includes(q)
    );
  });
}

/** Worst health first, then most offline, then most alerts, then name -- the triage order. */
export function triageSort(rows: SiteOverviewRow[]): SiteOverviewRow[] {
  return rows
    .slice()
    .sort(
      (a, b) =>
        HEALTH_RANK[a.health] - HEALTH_RANK[b.health] ||
        b.devices_offline - a.devices_offline ||
        b.alerts_open - a.alerts_open ||
        a.site_name.localeCompare(b.site_name),
    );
}

export interface OverviewSummary {
  sites: number;
  devices: number;
  offline: number;
  clients: number;
  attention: number;
  /** Distinct customers among the rows; the Customer column only shows when > 1. */
  customers: number;
}

/** Totals over the given rows (the summary line describes what the table shows). */
export function summarize(rows: SiteOverviewRow[]): OverviewSummary {
  return {
    sites: rows.length,
    devices: rows.reduce((n, r) => n + r.devices_total, 0),
    offline: rows.reduce((n, r) => n + r.devices_offline, 0),
    clients: rows.reduce((n, r) => n + r.clients_now, 0),
    attention: rows.filter(needsAttention).length,
    customers: new Set(rows.map((r) => r.org_id)).size,
  };
}

/** "1 site" / "3 sites". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The one-line summary: "N sites · X devices · Y offline · Z clients now". */
export function summaryLine(s: OverviewSummary): string {
  return [
    plural(s.sites, "site"),
    plural(s.devices, "device"),
    `${s.offline} offline`,
    `${s.clients} ${s.clients === 1 ? "client" : "clients"} now`,
  ].join(" · ");
}

/**
 * Firmware verdict -> short label. "Off baseline", never "Upgrade available": the backend does
 * not order version strings, so a differing device may be AHEAD of its approved build.
 */
export function firmwareLabel(fw: SiteOverviewRow["firmware"]): string {
  const labels: Record<SiteFirmwareStatus, string> = {
    current: "Up to date",
    off_baseline: `${fw.differs} off baseline`,
    unknown: "Not reported",
    no_baseline: "No baseline",
    none: "—",
  };
  return labels[fw.status];
}

/** Quote one CSV field (RFC 4180): wrap in quotes when it holds a comma, quote or newline. */
export function csvField(value: string | number | null | undefined): string {
  const s = value === null || value === undefined ? "" : String(value);
  // Neutralise spreadsheet formula injection: a cell starting with = + - @ is prefixed with '.
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** The table as CSV (the rows given, in the order given). Customer column only when needed. */
export function toCsv(rows: SiteOverviewRow[], includeCustomer: boolean): string {
  const header = [
    ...(includeCustomer ? ["Customer"] : []),
    "Site",
    "Location",
    "Health",
    "Online %",
    "Devices",
    "Offline",
    "Clients now",
    "Open alerts",
    "Firmware",
  ];
  const lines = [header.map(csvField).join(",")];
  for (const r of rows) {
    lines.push(
      [
        ...(includeCustomer ? [r.org_name] : []),
        r.site_name,
        r.location ?? "",
        r.health,
        r.online_pct ?? "",
        r.devices_total,
        r.devices_offline,
        r.clients_now,
        r.alerts_open,
        r.firmware.status === "none" ? "" : firmwareLabel(r.firmware),
      ]
        .map(csvField)
        .join(","),
    );
  }
  // CRLF per RFC 4180 -- Excel on Windows opens it cleanly.
  return lines.join("\r\n") + "\r\n";
}
